import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip
} from "reactstrap";
import { Link } from "react-router-dom";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchBanks, saveBank, changeBankStatus } from "store/actions";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import Swal from 'sweetalert2';
import useButtonAccess from "components/Common/useButtonAccess";

const BankMaster = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);
  const dispatch = useDispatch();
  const { banksList, loading, success, error } = useSelector((state) => state.BankMasterReducer || {});
  const [authDetails, setAuthDetails] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0]);
    const unitId = authUserDetails[0].unit_id;
    dispatch(fetchBanks(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchBanks(authDetails?.unit_id));
      setIsEdit(false);
      setEditId(null);
      formik.resetForm();
    }
  }, [authDetails, success, dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      bank_id: 0,
      bank_name: ""
    },
    validationSchema: Yup.object({
      bank_name: Yup.string().required("Bank Name is required")
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("bank_id", values.bank_id);
      formData.append("bank_name", values.bank_name);
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);

      dispatch(saveBank(formData));
    },
  });

  const handleEdit = (row) => {
    formik.setValues({
      bank_id: row.bank_id,
      bank_name: row.bank_name
    });
    setIsEdit(true);
    setEditId(row.bank_id);
  };

  const handleCancel = () => {
    formik.resetForm();
    setIsEdit(false);
    setEditId(null);
  };

  const handleToggleStatus = (row) => {
    Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete ${row.bank_name}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("bank_id", row.bank_id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeBankStatus(formData));
      }
    });
  };

  const columns = useMemo(() => {
    const actionColumn = (canEdit || canDelete)
      ? {
          Header: "Action",
          className: "status-col",
          applyHeaderClass: true,
          Cell: (cellProps) => {
            const { bank_id } = cellProps.row.original;
            return (
              <div className="d-flex gap-3">
                {canEdit && (
                  <Link
                    to="#"
                    className="text-success"
                    onClick={() => handleEdit(cellProps.row.original)}
                  >
                    <i
                      className="bx bxs-edit font-size-18"
                      id={`edit-tooltip-${bank_id}`}
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target={`edit-tooltip-${bank_id}`}
                    >
                      Edit
                    </UncontrolledTooltip>
                  </Link>
                )}

                {canDelete && (
                  <Link
                    to="#"
                    className="text-danger"
                    onClick={() => handleToggleStatus(cellProps.row.original)}
                  >
                    <i
                      className="mdi mdi-delete font-size-18"
                      id={`delete-tooltip-${bank_id}`}
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target={`delete-tooltip-${bank_id}`}
                    >
                      Delete
                    </UncontrolledTooltip>
                  </Link>
                )}
              </div>
            );
          },
        }
      : null;

    return [
      ...(actionColumn ? [actionColumn] : []),
      {
        Header: "S.No",
        Cell: ({ row }) => banksList.length - row.index,
        className: "serial-no",
        applyHeaderClass: true,
      },
      { Header: "Code", accessor: "bank_code" },
      { Header: "Bank Name", accessor: "bank_name" },
      { Header: "Created At", accessor: "created_dt" },
      { Header: "Created By", accessor: "created_by" },
    ];
  }, [banksList, canEdit, canDelete]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Setup" breadcrumbItem="Bank Master" />

          {(canCreate || canEdit) && (
            <Card>
              <CardBody>
                <Form onSubmit={formik.handleSubmit}>
                  <Row className="mb-3 g-3 align-items-center">
                    <Col md="9" lg="9" xl="9">
                      <FormGroup>
                        <Label for="bank_name">
                          Bank Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="bank_name"
                          name="bank_name"
                          type="text"
                          value={formik.values.bank_name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.bank_name &&
                            Boolean(formik.errors.bank_name)
                          }
                        />
                        {formik.touched.bank_name && formik.errors.bank_name && (
                          <div className="text-danger">{formik.errors.bank_name}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="3" lg="3" xl="3" className="d-flex align-items-center justify-content-end mt-4">
                      <Button type="submit" color="primary" className="btn button-save me-2">
                        <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                      </Button>
                      <Button type="button" color="secondary" onClick={handleCancel} className="btn button-cancel">
                        <i className="fas fa-sync me-1" /> Cancel
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody>
              <TableContainer
                columns={columns}
                data={banksList && banksList?.length > 0 ? banksList : []}
                isGlobalFilter={true}
                customPageSize={10}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BankMaster;
